<template>
  <div class="app">
    <main id="contents">
      <transition name="fade" mode="out-in">
        <router-view :items="items" />
      </transition>
    </main>
  </div>
</template>
<script>
import { withinPriceRange } from "./modules/withinPriceRange.js";

export default {
  name: "App",
  computed: {},
  data() {
    return {
      items: [],
      tables: {},
      forms: {},
      lastScrollY: 0,
      lastDirection: "",
    };
  },
  methods: {
    /**
     * 入力された値段から該当する価格レンジのキー名を返す
     */
    getMatchedPriceTableKey(price) {
      const priceTable = this.tables.price_range;
      const matchedPriceKey = Object.keys(priceTable).find((key) => {
        const rangeArray = priceTable[key];
        const isInRange = withinPriceRange(rangeArray, price);
        return isInRange;
      });
      return matchedPriceKey;
    },
    setMatchedCategory(bigan_name) {
      const categoryForms = this.forms["bigan_names"];
      const categoryFormId = Object.keys(categoryForms).find((key) => {
        const valuesArray = categoryForms[key].values;
        const isMatched = valuesArray.includes(bigan_name);
        return isMatched;
      });
      this.$store.dispatch("selectedFormIds/setRadioValue", {
        value: categoryFormId,
        target: "bigan_name",
      });
    },
    setMatchedPriceRange(matchedKey) {
      const priceRangeForm = this.forms["price_range"];
      const priceRangeFormIds = Object.keys(priceRangeForm).filter((key) => {
        const valuesArray = priceRangeForm[key].values;
        const isMatched = valuesArray.includes(matchedKey);
        return isMatched;
      });
      this.$store.dispatch("selectedFormIds/setCheckboxValue", {
        value: priceRangeFormIds,
        target: "price_range",
      });
    },
    setProductIdsInTheSameGroup(bigan_name, matchedPriceTableKey) {
      const priceTable = this.tables["price_range"];
      const matchedItems = this.items.filter((item) => {
        const isMatchedCategory = item.bigan_name === bigan_name;
        if (!isMatchedCategory) return false;

        const rangeArray = priceTable[matchedPriceTableKey];
        const isInRange = withinPriceRange(rangeArray, item.komi_kkk);
        return isInRange;
      });
      const matchedJanCodeArray = matchedItems.map((item) => item.jan_code);
      this.$store.dispatch("product/setSelectedProductIds", {
        value: matchedJanCodeArray,
      });
    },
    setFixedId(janCode) {
      this.$store.dispatch("product/setFixedId", { id: janCode });
    },
    showComparisonPage(itemData) {
      const { bigan_name, komi_kkk, jan_code } = itemData;
      this.setMatchedCategory(bigan_name);
      const matchedPriceTableKey = this.getMatchedPriceTableKey(komi_kkk);
      this.setMatchedPriceRange(matchedPriceTableKey);
      this.setProductIdsInTheSameGroup(bigan_name, matchedPriceTableKey);
      this.setFixedId(jan_code);
      this.$router.push("/comparison");
    },
    detectScrollDirection() {
      const scrollY = window.scrollY;
      const direction =
        scrollY > this.lastScrollY ? "scroll-down" : "scroll-up";
      if (direction !== this.lastDirection) {
        this.$store.dispatch("setScrollDirection", { value: direction });
        this.lastDirection = direction;
      }
      this.lastScrollY = scrollY;
    },
    handleScroll() {
      this.detectScrollDirection();
    },
  },
  async created() {
    window.addEventListener("scroll", this.handleScroll.bind(this));
    const response = await fetch(
      process.env.NODE_ENV === "production"
        ? "./assets/json/data.json"
        : "/json/data.json"
    ).then((response) => response.json());
    const { data, tables, form } = response;
    this.items = data;
    this.tables = tables;
    this.forms = form;
    this.$store.dispatch("jsonData/setTables", tables);
    this.$store.dispatch("jsonData/setForms", form);

    const janCodeParam = this.$route.query.jcd;
    if (!janCodeParam) return;

    const itemData = this.items.find((item) => item.jan_code === janCodeParam);
    if (itemData) this.showComparisonPage(itemData);
  },
  destroyed() {
    window.removeEventListener("scroll", this.handleScroll.bind(this));
  },
};
</script>
<style lang="scss">
@import url("https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500&family=Oswald&display=swap");
.app {
  /* @import "./assets/scss/base/reset"; */
  font-family: "Hiragino Kaku Gothic Pro", "Hiragino Sans", "Noto Sans JP",
    "Helvetica Neue", Arial, Meiryo, sans-serif;
  font-weight: 400;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  font-size: 13px;
  line-height: 1.8;
  color: #333333;
  position: relative;
  /* font-feature-settings: "palt"; */

  *,
  *::before,
  *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  ul,
  ol {
    list-style-type: none;
  }

  img {
    display: block;
    width: 100%;
    height: auto;
  }

  button {
    background-color: transparent;
    border: none;
    cursor: pointer;
    outline: none;
  }

  .pc-dn {
    @media screen and (min-width: $breakpoint) {
      display: none !important;
    }
  }
  .sp-dn {
    @media screen and (max-width: $breakpoint - 1) {
      display: none !important;
    }
  }
}

.fade,
.fade-fast {
  &-enter-active {
    transition: opacity 0.5s $easing-color;
  }
  &-leave-active {
    transition: opacity 0.3s $easing-color;
  }
  &-enter,
  &-leave-to {
    opacity: 0;
  }
  &-enter-to,
  &-leave {
    opacity: 1;
  }
}

.fade-fast {
  &-enter-active {
    transition: opacity 0.2s $easing-color;
  }
  &-leave-active {
    transition: opacity 0.1s $easing-color;
  }
}

.checkbox {
  width: 100%;
  position: relative;

  .checkbox-input {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;

    &:checked + .checkbox-title {
      &::after {
        content: "";
        display: block;
        position: absolute;
        width: 11px;
        height: 13px;
        background: url("./assets/images/_parts/other/check.svg") no-repeat
          center/contain;
        left: 5px;
        top: -1px;
      }
    }
  }

  .checkbox-title {
    display: flex;
    position: relative;
    align-items: center;
    width: 100%;
    height: 100%;
    line-height: 1.2;
    cursor: pointer;

    &::before {
      $size: 15px;
      content: "";
      position: relative;
      top: 0px;
      display: block;
      margin-right: 5px;
      width: $size;
      height: $size;
      border-radius: 3px;
      background-color: #fff;
      border: 1px solid #dddddd;
      flex-shrink: 0;
    }
  }

  @include media() {
    .checkbox-input {
      &:checked + .checkbox-title {
        &::after {
          width: vw(9);
          height: vw(11);
          left: vw(5);
          top: 0px;
        }
      }
    }

    .checkbox-title {
      line-height: 1.2;
      align-items: flex-start;
      font-size: vw(11);

      &::before {
        $size: vw(13);
        top: 0; //vw(0);
        margin-right: vw(8);
        width: $size;
        height: $size;
        border-radius: vw(3);
      }
    }
  }
}
</style>
